import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ShieldCheck, CreditCard, ArrowRight, Building2, MapPin } from 'lucide-react';
export const Checkout = () => {
  const navigate = useNavigate();
  const handlePay = (e: React.FormEvent) => {
    e.preventDefault();
    navigate('/payment-success');
  };
  return <div className="min-h-screen bg-neutral-light pt-24 pb-20" data-id="element-1806">
      <div className="container mx-auto px-4 md:px-6 max-w-5xl" data-id="element-1807">
        <div className="mb-8" data-id="element-1808">
          <Link to="/list-property" className="text-sm font-bold text-text-light hover:text-primary-900 mb-4 inline-block" data-id="element-1809">
            ← Back to Listing Setup
          </Link>
          <h1 className="text-3xl font-serif font-bold text-primary-900 mb-2" data-id="element-1810">
            Complete Payment
          </h1>
          <p className="text-text-light" data-id="element-1811">
            Secure payment for your property listing and verification.
          </p>
        </div>

        <div className="grid lg:grid-cols-5 gap-8" data-id="element-1812">
          {/* Left: Summary */}
          <div className="lg:col-span-2 space-y-6" data-id="element-1813">
            <div className="bg-white rounded-2xl shadow-soft border border-neutral-200 p-6" data-id="element-1814">
              <h3 className="font-serif font-bold text-lg text-primary-900 mb-4 border-b border-neutral-100 pb-4" data-id="element-1815">
                Order Summary
              </h3>

              <div className="flex gap-4 mb-6" data-id="element-1816">
                <div className="w-16 h-16 rounded-xl bg-neutral-100 flex items-center justify-center shrink-0" data-id="element-1817">
                  <Building2 className="text-neutral-400" size={24} data-id="element-1818" />
                </div>
                <div data-id="element-1819">
                  <h4 className="font-bold text-text text-sm mb-1" data-id="element-1820">
                    Luxury 5-Bed Duplex
                  </h4>
                  <p className="text-xs text-text-light flex items-center gap-1" data-id="element-1821">
                    <MapPin size={12} data-id="element-1822" /> Maitama District
                  </p>
                  <span className="inline-block mt-2 px-2 py-0.5 bg-secondary/10 text-secondary-dark text-[10px] font-bold rounded uppercase" data-id="element-1823">
                    Tier 1 Listing
                  </span>
                </div>
              </div>

              <div className="space-y-3 text-sm mb-2 pb-2" data-id="element-1824">
                <div className="flex justify-between text-text-light" data-id="element-1825">
                  <span data-id="element-1826">Tier 1 — Individual Listing</span>
                  <span className="font-medium text-text" data-id="element-1827">₦300,000</span>
                </div>
                <div className="flex justify-between text-text-light" data-id="element-1828">
                  <span data-id="element-1829">Verification Processing</span>
                  <span className="font-medium text-text" data-id="element-1830">Included</span>
                </div>
                <div className="flex justify-between text-text-light" data-id="element-1831">
                  <span data-id="element-1832">VAT (7.5%)</span>
                  <span className="font-medium text-text" data-id="element-1833">₦22,500</span>
                </div>
              </div>
              <p className="text-[11px] italic text-text-light leading-relaxed mb-6 pb-6 border-b border-neutral-100" data-id="element-1834">
                Real estate companies receive a reduced rate of ₦250,000 per
                home in estate developments. Tier 2 and Tier 3 fees apply
                elsewhere — see pricing in your seller dashboard.
              </p>

              <div className="flex justify-between items-end" data-id="element-1835">
                <span className="font-bold text-text" data-id="element-1836">Total Due</span>
                <span className="text-2xl font-bold text-primary-900" data-id="element-1837">
                  ₦322,500
                </span>
              </div>
            </div>

            <div className="bg-primary-50 rounded-2xl border border-primary-100 p-5 flex gap-3" data-id="element-1838">
              <ShieldCheck className="text-primary-900 shrink-0" size={24} data-id="element-1839" />
              <p className="text-xs text-primary-900 leading-relaxed" data-id="element-1840">
                <strong data-id="element-1841">Secure Transaction.</strong> Your payment is processed
                securely. The listing fee covers AGIS/FCDA verification and
                platform visibility for 90 days.
              </p>
            </div>
          </div>

          {/* Right: Payment Methods */}
          <div className="lg:col-span-3" data-id="element-1842">
            <div className="bg-white rounded-2xl shadow-soft border border-neutral-200 p-6 md:p-8" data-id="element-1843">
              <h3 className="font-serif font-bold text-lg text-primary-900 mb-6" data-id="element-1844">
                Payment Method
              </h3>

              <form onSubmit={handlePay} className="space-y-6" data-id="element-1845">
                <div className="space-y-3" data-id="element-1846">
                  <label className="flex items-center justify-between p-4 border-2 border-secondary bg-secondary/5 rounded-xl cursor-pointer" data-id="element-1847">
                    <div className="flex items-center gap-3" data-id="element-1848">
                      <input type="radio" name="payment" defaultChecked className="text-secondary focus:ring-secondary w-4 h-4" data-id="element-1849" />
                      <span className="font-bold text-text" data-id="element-1850">
                        Paystack (Card/Transfer)
                      </span>
                    </div>
                    <img src="https://upload.wikimedia.org/wikipedia/commons/0/0b/Paystack_Logo.png" alt="Paystack" className="h-5 object-contain" data-id="element-1851" />
                  </label>

                  <label className="flex items-center justify-between p-4 border border-neutral-200 rounded-xl cursor-pointer hover:bg-neutral-50 transition-colors" data-id="element-1852">
                    <div className="flex items-center gap-3" data-id="element-1853">
                      <input type="radio" name="payment" className="text-secondary focus:ring-secondary w-4 h-4" data-id="element-1854" />
                      <span className="font-bold text-text" data-id="element-1855">Flutterwave</span>
                    </div>
                    <img src="https://upload.wikimedia.org/wikipedia/commons/thumb/a/a4/Flutterwave_Logo.svg/1200px-Flutterwave_Logo.svg.png" alt="Flutterwave" className="h-5 object-contain grayscale opacity-70" data-id="element-1856" />
                  </label>

                  <label className="flex items-center justify-between p-4 border border-neutral-200 rounded-xl cursor-pointer hover:bg-neutral-50 transition-colors" data-id="element-1857">
                    <div className="flex items-center gap-3" data-id="element-1858">
                      <input type="radio" name="payment" className="text-secondary focus:ring-secondary w-4 h-4" data-id="element-1859" />
                      <span className="font-bold text-text" data-id="element-1860">
                        Direct Bank Transfer
                      </span>
                    </div>
                    <CreditCard className="text-neutral-400" size={24} data-id="element-1861" />
                  </label>
                </div>

                <div className="pt-6 border-t border-neutral-100" data-id="element-1862">
                  <label className="block text-sm font-bold text-text mb-2" data-id="element-1863">
                    Promo Code (Optional)
                  </label>
                  <div className="flex gap-2" data-id="element-1864">
                    <input type="text" placeholder="Enter code" className="flex-grow px-4 py-3 rounded-xl border border-neutral-200 bg-neutral-50 focus:bg-white focus:ring-2 focus:ring-primary-900 outline-none" data-id="element-1865" />
                    <button type="button" className="px-6 py-3 bg-neutral-200 text-text font-bold rounded-xl hover:bg-neutral-300 transition-colors" data-id="element-1866">
                      Apply
                    </button>
                  </div>
                </div>

                <button type="submit" className="w-full bg-primary-900 hover:bg-primary-800 text-white py-4 rounded-xl font-bold transition-colors flex items-center justify-center gap-2 shadow-lg mt-8" data-id="element-1867">
                  Pay ₦322,500 <ArrowRight size={18} data-id="element-1868" />
                </button>
                <p className="text-center text-xs text-text-light flex items-center justify-center gap-1" data-id="element-1869">
                  <ShieldCheck size={12} data-id="element-1870" /> Payments are encrypted and secure
                </p>
              </form>
            </div>
          </div>
        </div>
      </div>
    </div>;
};